import { AutomationFolder } from '@kizen/api/automation';

export type AutomationOption = {
  value: string;
  label: string;
  data: { id: string; name: string };
  folderId: string | null;
};

export type AutomationNode = {
  id: string;
  name: string;
  automations: AutomationOption[];
  children: AutomationNode[];
};

export const getAllAutomationsInNode = (
  node: AutomationNode
): AutomationOption[] => [
  ...node.automations,
  ...node.children.flatMap(getAllAutomationsInNode),
];

export const getAllFolderIdsInNode = (node: AutomationNode): string[] => [
  node.id,
  ...node.children.flatMap(getAllFolderIdsInNode),
];

export const buildAutomationTree = (
  folders: AutomationFolder[],
  automationOptions: AutomationOption[]
) => {
  if (!folders || folders.length === 0) {
    return { rootAutomations: automationOptions, folderTree: [] };
  }
  const rootFolder = folders.find((f) => f.parent_folder_id === null);
  if (!rootFolder) {
    return { rootAutomations: automationOptions, folderTree: [] };
  }

  const automationsByFolderId: Record<string, AutomationOption[]> = {};
  automationOptions.forEach((opt) => {
    const folderId = opt.folderId || rootFolder.id;
    if (!automationsByFolderId[folderId]) automationsByFolderId[folderId] = [];
    automationsByFolderId[folderId].push(opt);
  });

  const childFoldersByParentId: Record<string, AutomationFolder[]> = {};
  folders.forEach((f) => {
    if (f.parent_folder_id !== null) {
      if (!childFoldersByParentId[f.parent_folder_id])
        childFoldersByParentId[f.parent_folder_id] = [];
      childFoldersByParentId[f.parent_folder_id].push(f);
    }
  });

  const buildNode = (folder: AutomationFolder): AutomationNode => ({
    id: folder.id,
    name: folder.name,
    automations: automationsByFolderId[folder.id] || [],
    children: (childFoldersByParentId[folder.id] || []).map(buildNode),
  });

  return {
    rootAutomations: automationsByFolderId[rootFolder.id] || [],
    folderTree: (childFoldersByParentId[rootFolder.id] || []).map(buildNode),
  };
};
